import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../auth/AuthContext';
import { useManagerPayrollAccess } from '../../hooks/useManagerPayrollAccess';
import { IcoLock, IcoUsers, IcoBriefcase, IcoTrash } from '../dashboard/DashIcons';
import ConfirmDeleteModal from '../modals/ConfirmDeleteModal';

const PAY_TYPES = ['hourly', 'salary', 'bonus', 'commission', 'other'];

const PAY_TYPE_LABEL = {
  hourly:     'Hourly',
  salary:     'Salary',
  bonus:      'Bonus',
  commission: 'Commission',
  other:      'Other',
};

function fmt(n) {
  return Number(n || 0).toLocaleString('en-US', { style: 'currency', currency: 'USD' });
}

const EMPTY_FORM = {
  managerId:      '',
  payPeriodLabel: '',
  payPeriodStart: '',
  payPeriodEnd:   '',
  payType:        'hourly',
  hoursWorked:    '',
  rate:           '',
  grossPay:       '',
  notes:          '',
};

export default function ManagerPayrollPage() {
  const { authFetch } = useAuth();
  const { hasAccess, isOwner, loading: accessLoading } = useManagerPayrollAccess();

  const [entries,  setEntries]  = useState([]);
  const [managers, setManagers] = useState([]);
  const [accessList, setAccessList] = useState([]);
  const [eligibleUsers, setEligibleUsers] = useState([]);

  const [showForm,   setShowForm]   = useState(false);
  const [editId,     setEditId]     = useState(null);
  const [form,       setForm]       = useState(EMPTY_FORM);
  const [saving,     setSaving]     = useState(false);
  const [deleting,   setDeleting]   = useState(null);
  const [formError,  setFormError]  = useState('');
  const [filterMgr,  setFilterMgr]  = useState('');
  const [showAccess, setShowAccess] = useState(false);
  const [grantId,    setGrantId]    = useState('');
  const [confirmDelete, setConfirmDelete] = useState(null);

  // ── Data fetching ─────────────────────────────────────────────────────────

  const loadEntries = useCallback(async () => {
    const params = filterMgr ? `?managerId=${filterMgr}` : '';
    const res = await authFetch(`/api/manager-payroll${params}`);
    if (res.ok) setEntries(await res.json());
  }, [authFetch, filterMgr]);

  const loadManagers = useCallback(async () => {
    const res = await authFetch('/api/manager-payroll/managers');
    if (res.ok) setManagers(await res.json());
  }, [authFetch]);

  const loadAccessList = useCallback(async () => {
    const res = await authFetch('/api/manager-payroll/access');
    if (res.ok) setAccessList(await res.json());
  }, [authFetch]);

  const loadEligibleUsers = useCallback(async () => {
    // Re-use the managers endpoint — it returns superadmins/adminassistants too
    const res = await authFetch('/api/manager-payroll/managers');
    if (res.ok) setEligibleUsers(await res.json());
  }, [authFetch]);

  useEffect(() => {
    if (!hasAccess) return;
    loadEntries();
    loadManagers();
    if (isOwner) { loadAccessList(); loadEligibleUsers(); }
  }, [hasAccess, isOwner, loadEntries, loadManagers, loadAccessList, loadEligibleUsers]);

  useEffect(() => {
    if (hasAccess) loadEntries();
  }, [filterMgr]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Auto-calculate grossPay for hourly ───────────────────────────────────

  useEffect(() => {
    if (form.payType === 'hourly' && form.hoursWorked !== '' && form.rate !== '') {
      const gross = (parseFloat(form.hoursWorked) || 0) * (parseFloat(form.rate) || 0);
      setForm(f => ({ ...f, grossPay: gross.toFixed(2) }));
    }
  }, [form.hoursWorked, form.rate, form.payType]);

  // ── Form helpers ─────────────────────────────────────────────────────────

  function openNew() {
    setForm(EMPTY_FORM);
    setEditId(null);
    setFormError('');
    setShowForm(true);
  }

  function openEdit(entry) {
    setForm({
      managerId:      entry.managerId?._id || entry.managerId,
      payPeriodLabel: entry.payPeriodLabel || '',
      payPeriodStart: entry.payPeriodStart?.slice(0, 10) || '',
      payPeriodEnd:   entry.payPeriodEnd?.slice(0, 10)   || '',
      payType:        entry.payType  || 'hourly',
      hoursWorked:    entry.hoursWorked ?? '',
      rate:           entry.rate     ?? '',
      grossPay:       entry.grossPay ?? '',
      notes:          entry.notes    || '',
    });
    setEditId(entry._id);
    setFormError('');
    setShowForm(true);
  }

  function closeForm() {
    setShowForm(false);
    setEditId(null);
    setForm(EMPTY_FORM);
    setFormError('');
  }

  function handleFormChange(e) {
    const { name, value } = e.target;
    setForm(f => ({ ...f, [name]: value }));
  }

  async function handleSave(e) {
    e.preventDefault();
    setFormError('');

    if (!form.managerId)      return setFormError('Please select a manager.');
    if (!form.payPeriodStart) return setFormError('Pay period start date is required.');
    if (!form.payPeriodEnd)   return setFormError('Pay period end date is required.');
    if (!form.grossPay)       return setFormError('Gross pay is required.');

    const mgr = managers.find(m => m._id === form.managerId);
    const label = form.payPeriodLabel ||
      `${new Date(form.payPeriodStart).toLocaleDateString()} – ${new Date(form.payPeriodEnd).toLocaleDateString()}`;

    const body = {
      ...form,
      managerName: mgr ? `${mgr.firstName} ${mgr.lastName}` : '',
      payPeriodLabel: label,
      grossPay:    parseFloat(form.grossPay),
      hoursWorked: parseFloat(form.hoursWorked) || 0,
      rate:        parseFloat(form.rate)        || 0,
    };

    setSaving(true);
    try {
      const url    = editId ? `/api/manager-payroll/${editId}` : '/api/manager-payroll';
      const method = editId ? 'PUT' : 'POST';
      const res    = await authFetch(url, { method, body: JSON.stringify(body), headers: { 'Content-Type': 'application/json' } });
      if (!res.ok) {
        const d = await res.json();
        setFormError(d.message || 'Save failed');
        return;
      }
      closeForm();
      loadEntries();
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(id) {
    setDeleting(id);
    try {
      await authFetch(`/api/manager-payroll/${id}`, { method: 'DELETE' });
      setEntries(prev => prev.filter(e => e._id !== id));
    } finally {
      setDeleting(null);
    }
  }

  async function handleGrantAccess() {
    if (!grantId) return;
    const res = await authFetch('/api/manager-payroll/access', {
      method:  'POST',
      headers: { 'Content-Type': 'application/json' },
      body:    JSON.stringify({ userId: grantId }),
    });
    if (res.ok) { setGrantId(''); loadAccessList(); }
  }

  async function handleRevokeAccess(userId) {
    await authFetch(`/api/manager-payroll/access/${userId}`, { method: 'DELETE' });
    loadAccessList();
  }

  // ── Render ────────────────────────────────────────────────────────────────

  if (accessLoading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '60vh', color: '#6b7280' }}>
        Loading…
      </div>
    );
  }

  if (!hasAccess) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '60vh', gap: 12, color: '#6b7280', textAlign: 'center' }}>
        <IcoLock size={40} color="#9ca3af"/>
        <h2 style={{ margin: 0, color: '#374151', fontSize: 20 }}>Access Restricted</h2>
        <p style={{ margin: 0, maxWidth: 400 }}>
          Manager payroll is private. Only the account owner and users they've granted access to can view this section.
        </p>
      </div>
    );
  }

  const totalGross = entries.reduce((s, e) => s + (e.grossPay || 0), 0);

  return (
    <div style={{ padding: '24px 32px', maxWidth: 1100, margin: '0 auto', fontFamily: 'system-ui, sans-serif' }}>

      {/* ── Header ── */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 28, gap: 16, flexWrap: 'wrap' }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
            <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: '#111827' }}>Manager Payroll</h1>
            <span style={{ background: '#fef3c7', color: '#92400e', fontSize: 11, fontWeight: 700, padding: '3px 10px', borderRadius: 99, letterSpacing: '.05em' }}>
              <IcoLock size={10}/> PRIVATE
            </span>
          </div>
          <p style={{ margin: 0, color: '#6b7280', fontSize: 14 }}>
            Confidential pay records for managers and staff. Not visible to other super admins.
          </p>
        </div>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          {isOwner && (
            <button
              onClick={() => setShowAccess(s => !s)}
              style={{ padding: '9px 16px', border: '1px solid #d1d5db', borderRadius: 8, background: showAccess ? '#f3f4f6' : 'white', color: '#374151', fontSize: 14, fontWeight: 600, cursor: 'pointer' }}
            >
              <IcoUsers size={14}/> Manage Access
            </button>
          )}
          <button
            onClick={openNew}
            style={{ padding: '9px 18px', background: '#16a34a', border: 'none', borderRadius: 8, color: 'white', fontSize: 14, fontWeight: 700, cursor: 'pointer' }}
          >
            + Add Pay Entry
          </button>
        </div>
      </div>

      {/* ── Access Management Panel (owner only) ── */}
      {isOwner && showAccess && (
        <div style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 12, padding: '20px 24px', marginBottom: 28 }}>
          <h3 style={{ margin: '0 0 4px', fontSize: 15, fontWeight: 700, color: '#92400e' }}>Access Control</h3>
          <p style={{ margin: '0 0 16px', fontSize: 13, color: '#b45309' }}>
            Only you (the account owner) can grant or revoke access. People with access can view and add pay entries but cannot manage access themselves.
          </p>

          {/* Current access list */}
          {accessList.length === 0 ? (
            <p style={{ fontSize: 13, color: '#92400e', margin: '0 0 16px' }}>No one else has access yet.</p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 16 }}>
              {accessList.map(u => (
                <div key={u._id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: 'white', border: '1px solid #fde68a', borderRadius: 8, padding: '10px 14px' }}>
                  <div>
                    <span style={{ fontWeight: 600, color: '#1f2937', fontSize: 14 }}>{u.firstName} {u.lastName}</span>
                    <span style={{ color: '#6b7280', fontSize: 13, marginLeft: 8 }}>{u.email}</span>
                  </div>
                  <button
                    onClick={() => handleRevokeAccess(u._id)}
                    style={{ padding: '5px 12px', border: '1px solid #fca5a5', borderRadius: 6, background: 'white', color: '#dc2626', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}
                  >
                    Revoke
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* Grant access */}
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <select
              value={grantId}
              onChange={e => setGrantId(e.target.value)}
              style={{ flex: 1, minWidth: 200, padding: '8px 12px', border: '1px solid #fde68a', borderRadius: 8, fontSize: 14, background: 'white' }}
            >
              <option value="">Select a user to grant access…</option>
              {eligibleUsers
                .filter(u => ['superadmin', 'admin', 'adminassistant', 'manager'].includes(u.role) && !accessList.find(a => a._id === u._id))
                .map(u => (
                  <option key={u._id} value={u._id}>{u.firstName} {u.lastName}</option>
                ))
              }
            </select>
            <button
              onClick={handleGrantAccess}
              disabled={!grantId}
              style={{ padding: '8px 18px', background: grantId ? '#d97706' : '#d1d5db', border: 'none', borderRadius: 8, color: 'white', fontSize: 14, fontWeight: 700, cursor: grantId ? 'pointer' : 'not-allowed' }}
            >
              Grant Access
            </button>
          </div>
        </div>
      )}

      {/* ── Summary stat ── */}
      <div style={{ display: 'flex', gap: 16, marginBottom: 24, flexWrap: 'wrap' }}>
        <div style={{ background: 'white', border: '1px solid #e5e7eb', borderRadius: 12, padding: '16px 24px', minWidth: 160 }}>
          <div style={{ fontSize: 12, color: '#6b7280', fontWeight: 600, letterSpacing: '.05em', textTransform: 'uppercase', marginBottom: 4 }}>Total Shown</div>
          <div style={{ fontSize: 26, fontWeight: 800, color: '#16a34a' }}>{fmt(totalGross)}</div>
          <div style={{ fontSize: 12, color: '#9ca3af', marginTop: 2 }}>{entries.length} {entries.length === 1 ? 'entry' : 'entries'}</div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <label style={{ fontSize: 13, color: '#374151', fontWeight: 500 }}>Filter by manager:</label>
          <select
            value={filterMgr}
            onChange={e => setFilterMgr(e.target.value)}
            style={{ padding: '7px 12px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 14 }}
          >
            <option value="">All managers</option>
            {managers.map(m => (
              <option key={m._id} value={m._id}>{m.firstName} {m.lastName}</option>
            ))}
          </select>
        </div>
      </div>

      {/* ── Entries table ── */}
      {entries.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '60px 20px', color: '#9ca3af', border: '1px dashed #e5e7eb', borderRadius: 12 }}>
          <IcoBriefcase size={36} color="#d1d5db"/>
          <div style={{ fontSize: 15, fontWeight: 600, color: '#6b7280', marginBottom: 6 }}>No pay entries yet</div>
          <div style={{ fontSize: 13 }}>Click "+ Add Pay Entry" to record a manager payment.</div>
        </div>
      ) : (
        <div style={{ background: 'white', border: '1px solid #e5e7eb', borderRadius: 12, overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
            <thead>
              <tr style={{ background: '#f9fafb', borderBottom: '1px solid #e5e7eb' }}>
                {['Manager', 'Pay Period', 'Type', 'Hours', 'Rate', 'Gross Pay', 'Notes', ''].map(h => (
                  <th key={h} style={{ padding: '12px 16px', textAlign: 'left', fontSize: 12, fontWeight: 700, color: '#6b7280', letterSpacing: '.05em', textTransform: 'uppercase', whiteSpace: 'nowrap' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {entries.map((entry, i) => (
                <tr key={entry._id} style={{ borderBottom: i < entries.length - 1 ? '1px solid #f3f4f6' : 'none', background: 'white' }}
                  onMouseEnter={e => e.currentTarget.style.background = '#f9fafb'}
                  onMouseLeave={e => e.currentTarget.style.background = 'white'}
                >
                  <td style={{ padding: '14px 16px', fontWeight: 600, color: '#111827' }}>
                    {entry.managerId?.firstName
                      ? `${entry.managerId.firstName} ${entry.managerId.lastName}`
                      : entry.managerName}
                  </td>
                  <td style={{ padding: '14px 16px', color: '#374151' }}>{entry.payPeriodLabel}</td>
                  <td style={{ padding: '14px 16px' }}>
                    <span style={{ background: '#f3f4f6', color: '#374151', padding: '3px 10px', borderRadius: 99, fontSize: 12, fontWeight: 600 }}>
                      {PAY_TYPE_LABEL[entry.payType] || entry.payType}
                    </span>
                  </td>
                  <td style={{ padding: '14px 16px', color: '#374151' }}>{entry.hoursWorked ? entry.hoursWorked.toFixed(2) : '—'}</td>
                  <td style={{ padding: '14px 16px', color: '#374151' }}>{entry.rate ? fmt(entry.rate) : '—'}</td>
                  <td style={{ padding: '14px 16px', fontWeight: 700, color: '#16a34a' }}>{fmt(entry.grossPay)}</td>
                  <td style={{ padding: '14px 16px', color: '#6b7280', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{entry.notes || '—'}</td>
                  <td style={{ padding: '14px 16px', whiteSpace: 'nowrap' }}>
                    <button
                      onClick={() => openEdit(entry)}
                      style={{ padding: '5px 12px', border: '1px solid #e5e7eb', borderRadius: 6, background: 'white', color: '#374151', fontSize: 12, fontWeight: 600, cursor: 'pointer', marginRight: 6 }}
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => setConfirmDelete({ id: entry._id })}
                      disabled={deleting === entry._id}
                      style={{ padding: '5px 12px', border: '1px solid #fca5a5', borderRadius: 6, background: 'white', color: '#dc2626', fontSize: 12, fontWeight: 600, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 4 }}
                    >
                      <IcoTrash size={13} color="#dc2626"/>
                      {deleting === entry._id ? '…' : ''}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* ── Add / Edit modal ── */}
      {showForm && (
        <div
          onClick={closeForm}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{ background: 'white', borderRadius: 16, padding: '28px 32px', width: '100%', maxWidth: 520, boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}
          >
            <h2 style={{ margin: '0 0 20px', fontSize: 18, fontWeight: 700, color: '#111827' }}>
              {editId ? 'Edit Pay Entry' : 'Add Pay Entry'}
            </h2>
            <form onSubmit={handleSave}>
              <div style={{ display: 'grid', gap: 14 }}>

                <div>
                  <label style={labelStyle}>Manager *</label>
                  <select name="managerId" value={form.managerId} onChange={handleFormChange} style={inputStyle} required>
                    <option value="">Select manager…</option>
                    {managers.map(m => (
                      <option key={m._id} value={m._id}>{m.firstName} {m.lastName} ({m.role})</option>
                    ))}
                  </select>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                  <div>
                    <label style={labelStyle}>Period Start *</label>
                    <input type="date" name="payPeriodStart" value={form.payPeriodStart} onChange={handleFormChange} style={inputStyle} required />
                  </div>
                  <div>
                    <label style={labelStyle}>Period End *</label>
                    <input type="date" name="payPeriodEnd" value={form.payPeriodEnd} onChange={handleFormChange} style={inputStyle} required />
                  </div>
                </div>

                <div>
                  <label style={labelStyle}>Period Label (optional)</label>
                  <input type="text" name="payPeriodLabel" value={form.payPeriodLabel} onChange={handleFormChange}
                    placeholder="e.g. July 1–15, 2026 (auto-generated if blank)"
                    style={inputStyle} />
                </div>

                <div>
                  <label style={labelStyle}>Pay Type</label>
                  <select name="payType" value={form.payType} onChange={handleFormChange} style={inputStyle}>
                    {PAY_TYPES.map(t => <option key={t} value={t}>{PAY_TYPE_LABEL[t]}</option>)}
                  </select>
                </div>

                {form.payType === 'hourly' && (
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                    <div>
                      <label style={labelStyle}>Hours Worked</label>
                      <input type="number" name="hoursWorked" value={form.hoursWorked} onChange={handleFormChange}
                        min="0" step="0.25" placeholder="0.00" style={inputStyle} />
                    </div>
                    <div>
                      <label style={labelStyle}>Hourly Rate ($)</label>
                      <input type="number" name="rate" value={form.rate} onChange={handleFormChange}
                        min="0" step="0.01" placeholder="0.00" style={inputStyle} />
                    </div>
                  </div>
                )}

                <div>
                  <label style={labelStyle}>Gross Pay ($) *</label>
                  <input type="number" name="grossPay" value={form.grossPay} onChange={handleFormChange}
                    min="0" step="0.01" placeholder="0.00" style={{ ...inputStyle, fontWeight: 700, color: '#16a34a' }} required />
                  {form.payType === 'hourly' && form.hoursWorked && form.rate && (
                    <p style={{ margin: '4px 0 0', fontSize: 12, color: '#16a34a' }}>
                      Auto-calculated: {form.hoursWorked} hrs × {fmt(form.rate)} = {fmt(form.grossPay)}
                    </p>
                  )}
                </div>

                <div>
                  <label style={labelStyle}>Private Notes</label>
                  <textarea name="notes" value={form.notes} onChange={handleFormChange}
                    rows={3} placeholder="Internal notes (not visible to the manager)"
                    style={{ ...inputStyle, resize: 'vertical', height: 80 }} />
                </div>

                {formError && (
                  <p style={{ margin: 0, color: '#dc2626', fontSize: 13 }}>{formError}</p>
                )}

                <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', paddingTop: 4 }}>
                  <button type="button" onClick={closeForm}
                    style={{ padding: '10px 20px', border: '1px solid #e5e7eb', borderRadius: 8, background: 'white', color: '#374151', fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>
                    Cancel
                  </button>
                  <button type="submit" disabled={saving}
                    style={{ padding: '10px 24px', border: 'none', borderRadius: 8, background: saving ? '#86efac' : '#16a34a', color: 'white', fontSize: 14, fontWeight: 700, cursor: saving ? 'not-allowed' : 'pointer' }}>
                    {saving ? 'Saving…' : (editId ? 'Save Changes' : 'Add Entry')}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}
    {confirmDelete && (
      <ConfirmDeleteModal
        message="Are you sure you want to delete this pay entry? This cannot be undone."
        onConfirm={() => { handleDelete(confirmDelete.id); setConfirmDelete(null); }}
        onCancel={() => setConfirmDelete(null)}
      />
    )}
    </div>
  );
}

const labelStyle = {
  display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 5,
};

const inputStyle = {
  width: '100%', padding: '9px 12px', border: '1px solid #d1d5db', borderRadius: 8,
  fontSize: 14, color: '#111827', background: 'white', boxSizing: 'border-box',
};
